import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { Tv, Film, LogOut, Plus, Eye, Users, Star, ThumbsUp, Meh, ThumbsDown, List, MoreVertical, Trash2, Share2, ChevronRight, Sparkles, Lock, Mic, Info, ArrowRight, BookOpen, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { MobileNav } from '../components/MobileNav';
import { clearAuth } from '../lib/api';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SortaLogo = ({ size = "default" }) => {
  const sizeClasses = { small: "h-8", default: "h-10", large: "h-14" };
  return (
    <img 
      src="https://customer-assets.emergentagent.com/job_series-ranker-1/artifacts/barwtr5h_image.jpg"
      alt="Sorta"
      className={`${sizeClasses[size]} object-contain rounded-lg`}
    />
  );
};

export default function PodcastsPage({ user }) {
  const navigate = useNavigate();
  const [watchlists, setWatchlists] = useState([]);
  const [listenedPodcasts, setListenedPodcasts] = useState([]);
  const [stats, setStats] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newListTitle, setNewListTitle] = useState('');
  const [newListDescription, setNewListDescription] = useState('');
  const [newListType, setNewListType] = useState('personal');
  
  // Recommendation interaction state
  const [selectedPodcast, setSelectedPodcast] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isAddToListOpen, setIsAddToListOpen] = useState(false);
  const [addingToList, setAddingToList] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [listsRes, listenedRes, statsRes, recsRes] = await Promise.all([
        axios.get(`${API}/podcast-lists`, { withCredentials: true }),
        axios.get(`${API}/listened-podcasts`, { withCredentials: true }),
        axios.get(`${API}/listened-podcasts/stats`, { withCredentials: true }),
        axios.get(`${API}/recommendations/podcasts`, { withCredentials: true }).catch(() => ({ data: null }))
      ]);
      setWatchlists(listsRes.data || []);
      setListenedPodcasts(listenedRes.data.podcasts || []);
      setStats(statsRes.data);
      setRecommendations(recsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      clearAuth();
      navigate('/');
    } catch (error) {
      clearAuth();
      navigate('/');
    }
  };

  const handleCreateList = async () => {
    if (!newListTitle.trim()) {
      toast.error('Please enter a title');
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/podcast-lists`,
        { 
          title: newListTitle, 
          description: newListDescription, 
          list_type: newListType,
          media_category: 'podcast'
        },
        { withCredentials: true }
      );
      setWatchlists([...watchlists, response.data]);
      setIsCreateDialogOpen(false);
      setNewListTitle('');
      setNewListDescription('');
      setNewListType('personal');
      toast.success(newListType === 'joint' ? 'Joint list created! Share it with friends.' : 'List created!');
    } catch (error) {
      toast.error('Failed to create list');
    }
  };

  const handleDeleteList = async (listId) => {
    try {
      await axios.delete(`${API}/podcast-lists/${listId}`, { withCredentials: true });
      setWatchlists(watchlists.filter(l => l.id !== listId));
      toast.success('List deleted');
    } catch (error) {
      toast.error('Failed to delete list');
    }
  };

  const handleViewDetails = (podcast) => {
    setSelectedPodcast(podcast);
    setIsDetailsOpen(true);
  };

  const handleAddToList = (podcast) => {
    setSelectedPodcast(podcast);
    setIsAddToListOpen(true);
  };

  const handleAddToListConfirm = async (listId, position = 'bottom') => {
    if (!selectedPodcast || !listId) return;
    
    setAddingToList(true);
    try {
      await axios.post(`${API}/podcast-lists/${listId}/podcasts`, {
        podcast_id: selectedPodcast.podcast_id,
        title: selectedPodcast.title,
        publisher: selectedPodcast.publisher,
        image: selectedPodcast.image,
        description: selectedPodcast.description,
        genres: selectedPodcast.genres || [],
        position
      }, { withCredentials: true });
      
      toast.success(`Added "${selectedPodcast.title}" to list!`);
      setIsAddToListOpen(false);
      setSelectedPodcast(null);
      
      // Refresh watchlists
      const listsRes = await axios.get(`${API}/podcast-lists`, { withCredentials: true });
      setWatchlists(listsRes.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add to list');
    } finally {
      setAddingToList(false);
    }
  };

  // Count total podcasts across all lists
  const totalInLists = watchlists.reduce((sum, list) => sum + (list.podcasts?.length || 0), 0);

  // Check if recommendations unlocked (10+ listened)
  const recsUnlocked = recommendations?.unlocked || (stats?.total_listened >= 10);
  const progress = Math.min((recommendations?.listened_count || stats?.total_listened || 0) / 10 * 100, 100);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#3D4F6F] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#F87171] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#3D4F6F]">
      {/* Header - Solid Red Banner */}
      <header className="bg-[#F87171] sticky top-0 z-50 shadow-lg">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 sm:gap-6">
              {/* Mobile Menu */}
              <MobileNav user={user} onLogout={handleLogout} />
              
              <div className="flex items-center gap-2 sm:gap-3 cursor-pointer" onClick={() => navigate('/podcasts')}>
                <SortaLogo size="default" />
                <span className="hidden sm:block text-white font-outfit font-semibold text-xl">Media</span>
              </div>
              <nav className="hidden sm:flex items-center gap-1">
                {/* Social - First in nav */}
                <Button
                  variant="ghost"
                  onClick={() => navigate('/social')}
                  className="text-white/80 hover:text-white hover:bg-white/10 rounded-full"
                  data-testid="nav-social"
                >
                  <Users className="w-4 h-4 mr-2" />
                  Social
                </Button>
                {/* Messages */}
                <Button
                  variant="ghost"
                  onClick={() => navigate('/messages')}
                  className="text-white/80 hover:text-white hover:bg-white/10 rounded-full"
                  data-testid="nav-messages"
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Messages
                </Button>
                {(!user?.interests || user.interests.includes('tv')) && (
                  <Button
                    variant="ghost"
                    onClick={() => navigate('/tv-shows')}
                    className="text-white/80 hover:text-white hover:bg-white/10 rounded-full"
                  >
                    <Tv className="w-4 h-4 mr-2" />
                    TV Shows
                  </Button>
                )}
                {(!user?.interests || user.interests.includes('movies')) && (
                  <Button
                    variant="ghost"
                    onClick={() => navigate('/films')}
                    className="text-white/80 hover:text-white hover:bg-white/10 rounded-full"
                  >
                    <Film className="w-4 h-4 mr-2" />
                    Films
                  </Button>
                )}
                {(!user?.interests || user.interests.includes('podcasts')) && (
                  <Button
                    variant="ghost"
                    className="bg-white/20 text-white hover:bg-white/30 rounded-full"
                  >
                    <Mic className="w-4 h-4 mr-2" />
                    Podcasts
                  </Button>
                )}
                {(!user?.interests || user.interests.includes('books')) && (
                  <Button
                    variant="ghost"
                    onClick={() => navigate('/books')}
                    className="text-white/80 hover:text-white hover:bg-white/10 rounded-full"
                    data-testid="nav-books"
                  >
                    <BookOpen className="w-4 h-4 mr-2" />
                    Books
                  </Button>
                )}
              </nav>
            </div>
            
            {/* Desktop user menu */}
            <div className="hidden sm:flex items-center gap-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="rounded-full p-0 w-10 h-10" data-testid="user-menu-btn">
                    <Avatar className="border-2 border-white/30">
                      {user?.avatar_url ? (
                        <AvatarImage src={user.avatar_url} alt={user?.display_name || user?.name} />
                      ) : (
                        <AvatarImage src={user?.picture} alt={user?.name} />
                      )}
                      <AvatarFallback className="bg-white text-[#F87171]">
                        {(user?.display_name || user?.name)?.charAt(0) || 'U'}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <div className="px-3 py-2 border-b">
                    <p className="font-medium text-sm truncate">{user?.display_name || user?.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                  </div>
                  <DropdownMenuItem onClick={() => navigate('/profile')} className="cursor-pointer">
                    <Eye className="w-4 h-4 mr-2" />
                    View Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer">
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            
            {/* Mobile avatar */}
            <div className="sm:hidden">
              <Avatar className="border-2 border-white/30 h-9 w-9">
                {user?.avatar_url ? (
                  <AvatarImage src={user.avatar_url} alt={user?.display_name || user?.name} />
                ) : user?.picture ? (
                  <AvatarImage src={user?.picture} alt={user?.name} />
                ) : (
                  <AvatarFallback className="bg-white text-[#F87171] text-sm">
                    {(user?.display_name || user?.name)?.charAt(0) || 'U'}
                  </AvatarFallback>
                )}
              </Avatar>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Page Title */}
        <div className="flex items-center justify-between mb-6 sm:mb-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/90 rounded-xl flex items-center justify-center shadow-lg">
              <Mic className="w-5 h-5 sm:w-6 sm:h-6 text-[#F87171]" />
            </div>
            <div>
              <h1 className="font-outfit text-xl sm:text-2xl md:text-3xl font-bold text-white drop-shadow-lg">Podcasts</h1>
              <p className="text-white/80 text-sm sm:text-base">{totalInLists} in lists • {listenedPodcasts.length} listened</p>
            </div>
          </div>
        </div>

        {/* Three-Tile Layout - Stack on mobile */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          {/* Lists Tile */}
          <Card className="border-0 shadow-xl bg-white/95 backdrop-blur-sm">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="font-outfit text-lg text-[#3D4F6F] flex items-center gap-2">
                  <List className="w-5 h-5" />
                  My Lists
                </CardTitle>
                <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                  <DialogTrigger asChild>
                    <Button 
                      data-testid="create-podcast-list-btn"
                      size="sm"
                      className="bg-[#F87171] hover:bg-[#EF4444] text-white rounded-full"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      New List
                    </Button>
                  </DialogTrigger>
                  <DialogContent data-testid="create-podcast-list-dialog">
                    <DialogHeader>
                      <DialogTitle className="font-outfit text-[#3D4F6F]">Create Podcast List</DialogTitle>
                      <DialogDescription className="text-[#64748B]">
                        Create a personal list or a joint list to rank with friends.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div>
                        <Label className="text-sm font-medium text-[#3D4F6F] mb-2 block">List Type</Label>
                        <RadioGroup value={newListType} onValueChange={setNewListType} className="flex gap-4">
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="personal" id="personal-podcast" />
                            <Label htmlFor="personal-podcast" className="flex items-center gap-2 cursor-pointer">
                              <List className="w-4 h-4" />
                              Personal
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="joint" id="joint-podcast" />
                            <Label htmlFor="joint-podcast" className="flex items-center gap-2 cursor-pointer">
                              <Users className="w-4 h-4" />
                              Joint List
                            </Label>
                          </div>
                        </RadioGroup>
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-[#3D4F6F] mb-2 block">Title</Label>
                        <Input
                          data-testid="podcast-list-title-input"
                          placeholder="e.g., True Crime Favorites"
                          value={newListTitle}
                          onChange={(e) => setNewListTitle(e.target.value)}
                          className="border-slate-200"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-[#3D4F6F] mb-2 block">Description (optional)</Label>
                        <Textarea
                          data-testid="podcast-list-description-input"
                          placeholder="What's this list about?"
                          value={newListDescription}
                          onChange={(e) => setNewListDescription(e.target.value)}
                          className="border-slate-200"
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button 
                        data-testid="create-podcast-list-submit"
                        onClick={handleCreateList}
                        className="bg-[#F87171] hover:bg-[#EF4444] text-white"
                      >
                        Create List
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent className="space-y-3 max-h-[400px] overflow-y-auto">
              {watchlists.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Mic className="w-10 h-10 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">No podcast lists yet</p>
                  <p className="text-xs">Create one to start ranking!</p>
                </div>
              ) : (
                watchlists.map((list) => (
                  <div 
                    key={list.id}
                    className="group p-3 rounded-xl bg-slate-50 hover:bg-[#F87171]/10 cursor-pointer transition-all"
                    onClick={() => navigate(`/podcasts/list/${list.id}`)}
                    data-testid={`podcast-list-${list.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium text-[#3D4F6F] truncate">{list.title}</h3>
                          {list.list_type === 'joint' && (
                            <Users className="w-4 h-4 text-[#F87171] flex-shrink-0" />
                          )}
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {list.podcasts?.length || 0} podcasts
                        </p>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {list.list_type === 'joint' && (
                            <DropdownMenuItem onClick={(e) => {
                              e.stopPropagation();
                              navigator.clipboard.writeText(`${window.location.origin}/join/podcast/${list.share_code}`);
                              toast.success('Share link copied!');
                            }}>
                              <Share2 className="w-4 h-4 mr-2" />
                              Copy Share Link
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem 
                            onClick={(e) => { e.stopPropagation(); handleDeleteList(list.id); }}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Listened History Tile */}
          <Card className="border-0 shadow-xl bg-white/95 backdrop-blur-sm">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="font-outfit text-lg text-[#3D4F6F] flex items-center gap-2">
                  <Eye className="w-5 h-5" />
                  Listened
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/podcasts/listened')}
                  className="text-[#F87171] hover:text-[#EF4444]"
                >
                  View All <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {listenedPodcasts.length === 0 ? (
                <div className="text-center py-8">
                  <Eye className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-[#64748B]">No podcasts listened yet</p>
                  <p className="text-sm text-[#94A3B8] mt-1">
                    Mark podcasts as listened from your lists
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-3 max-h-[350px] overflow-y-auto">
                  {listenedPodcasts.slice(0, 9).map((podcast) => (
                    <ListenedPodcastCard key={podcast.podcast_id} podcast={podcast} />
                  ))}
                </div>
              )}
              {listenedPodcasts.length > 9 && (
                <p className="text-center text-[#64748B] text-sm mt-4">
                  +{listenedPodcasts.length - 9} more listened
                </p>
              )}
            </CardContent>
          </Card>

          {/* Discover/Recommendations Tile */}
          <Card className="border-0 shadow-xl bg-white/95 backdrop-blur-sm">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="font-outfit text-lg text-[#3D4F6F] flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-[#F87171]" />
                  Discover
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/discover/podcasts')}
                  className="text-[#F87171] hover:text-[#EF4444]"
                  data-testid="discover-podcasts-btn"
                >
                  Explore <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {!recsUnlocked ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-[#F87171]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Lock className="w-8 h-8 text-[#F87171]" />
                  </div>
                  <p className="text-[#3D4F6F] font-medium mb-2">Smart Recommendations Locked</p>
                  <p className="text-sm text-[#64748B] mb-4">
                    Listen to {10 - (recommendations?.listened_count || stats?.total_listened || 0)} more podcasts to unlock
                  </p>
                  <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden mb-4">
                    <div 
                      className="h-full bg-[#F87171] rounded-full transition-all"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <Button
                    onClick={() => navigate('/discover/podcasts')}
                    variant="outline"
                    className="rounded-full border-[#F87171] text-[#F87171] hover:bg-[#F87171] hover:text-white"
                  >
                    Browse All Podcasts
                  </Button>
                </div>
              ) : (
                <>
                  <p className="text-xs text-[#64748B] mb-1">
                    Based on podcasts you liked
                  </p>
                  {recommendations?.top_genres?.length > 0 && (
                    <p className="text-xs text-[#94A3B8] mb-3">
                      Your top genres: {recommendations.top_genres.slice(0, 3).join(', ')}
                    </p>
                  )}
                  <div className="grid grid-cols-3 gap-3 max-h-[300px] overflow-y-auto">
                    {recommendations?.recommendations?.slice(0, 6).map((podcast) => (
                      <div 
                        key={podcast.podcast_id}
                        className="cursor-pointer group text-center"
                        onClick={() => navigate(`/discover/podcasts?search=${encodeURIComponent(podcast.title)}`)}
                      >
                        <div className="aspect-square rounded-lg overflow-hidden mb-1 bg-slate-100">
                          {podcast.thumbnail ? (
                            <img 
                              src={podcast.thumbnail}
                              alt={podcast.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Mic className="w-6 h-6 text-slate-300" />
                            </div>
                          )}
                        </div>
                        <p className="text-xs font-medium text-[#3D4F6F] line-clamp-2 group-hover:text-[#F87171] transition-colors">
                          {podcast.title}
                        </p>
                      </div>
                    ))}
                  </div>
                  <Button
                    onClick={() => navigate('/discover/podcasts')}
                    className="w-full mt-4 bg-[#F87171] hover:bg-[#EF4444] text-white rounded-full"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    See All Recommendations
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Podcast Details Dialog */}
      <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{selectedPodcast?.title}</DialogTitle>
            <DialogDescription>{selectedPodcast?.publisher}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {selectedPodcast?.image && (
              <img 
                src={selectedPodcast.image} 
                alt={selectedPodcast.title} 
                className="w-full h-48 object-cover rounded-lg"
              />
            )}
            {selectedPodcast?.description && (
              <p className="text-sm text-[#64748B] line-clamp-4">{selectedPodcast.description}</p>
            )}
            <Button
              onClick={() => {
                setIsDetailsOpen(false);
                setIsAddToListOpen(true);
              }}
              className="w-full bg-[#F87171] hover:bg-[#EF4444] text-white rounded-full"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add to List
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add to List Dialog */}
      <Dialog open={isAddToListOpen} onOpenChange={setIsAddToListOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Add to List</DialogTitle>
            <DialogDescription>
              Choose a list for &ldquo;{selectedPodcast?.title}&rdquo;
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {watchlists.length === 0 ? (
              <p className="text-center py-4 text-[#64748B]">
                No lists yet. Create one first!
              </p>
            ) : (
              watchlists.map((list) => (
                <div
                  key={list.id}
                  className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 border border-slate-100"
                >
                  <div className="flex items-center gap-2">
                    <List className="w-4 h-4 text-[#64748B]" />
                    <span className="font-medium text-[#3D4F6F]">{list.title}</span>
                    <span className="text-xs text-[#94A3B8]">({list.podcasts?.length || 0})</span>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleAddToListConfirm(list.id, 'top')}
                      disabled={addingToList}
                      className="text-xs"
                    >
                      Top
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleAddToListConfirm(list.id, 'bottom')}
                      disabled={addingToList}
                      className="text-xs bg-[#F87171] hover:bg-[#EF4444] text-white"
                    >
                      Add
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ListenedPodcastCard({ podcast }) {
  const gradeConfig = {
    liked: { color: 'bg-[#F87171]', icon: <ThumbsUp className="w-2.5 h-2.5" /> },
    alright: { color: 'bg-amber-400', icon: <Meh className="w-2.5 h-2.5" /> },
    disliked: { color: 'bg-slate-400', icon: <ThumbsDown className="w-2.5 h-2.5" /> }
  };
  const grade = gradeConfig[podcast.grade] || gradeConfig.alright;

  return (
    <div className="group" data-testid={`listened-podcast-${podcast.podcast_id}`}>
      <div className="relative aspect-square rounded-lg overflow-hidden shadow-sm">
        {podcast.thumbnail ? (
          <img src={podcast.thumbnail} alt={podcast.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-slate-200 flex items-center justify-center">
            <Mic className="w-6 h-6 text-slate-400" />
          </div>
        )}
        <div className={`absolute top-1 right-1 ${grade.color} text-white rounded-full w-5 h-5 flex items-center justify-center`}>
          {grade.icon}
        </div>
      </div>
      <p className="mt-1 text-xs text-[#3D4F6F] truncate font-medium">{podcast.title}</p>
    </div>
  );
}
